/**
 * Buyer budget soft-warn vs pricing insight bands (D11 — advise, never hard-block).
 * Phase 5: tighter ratio when seller tight band (bandMin) is present.
 */

export type BuyerBudgetGuidanceInput = {
  maxPrice: number | null | undefined;
  /** Tight/seller band floor when present */
  bandMin?: number | null;
  /** Fallback market range min */
  rangeMin?: number | null;
  suggestedPrice?: number | null;
};

export type BuyerBudgetGuidanceResult = {
  showSoftWarn: boolean;
  /** Suggested raise target when warning */
  suggestedMax: number | null;
  message: string | null;
};

/**
 * When seller tight band (`bandMin`) is available — warn closer to market floor.
 * Phase 5 tune vs seller tight band (was 0.85 for all floors).
 */
export const BUYER_BUDGET_SOFT_WARN_RATIO = 0.9;

/** When only broad `rangeMin` is available (weaker comps). */
export const BUYER_BUDGET_SOFT_WARN_RATIO_RANGE = 0.85;

function resolveFloor(input: BuyerBudgetGuidanceInput): {
  floor: number;
  ratio: number;
} | null {
  const band = input.bandMin;
  if (typeof band === "number" && Number.isFinite(band) && band > 0) {
    return { floor: band, ratio: BUYER_BUDGET_SOFT_WARN_RATIO };
  }
  const range = input.rangeMin;
  if (typeof range === "number" && Number.isFinite(range) && range > 0) {
    return { floor: range, ratio: BUYER_BUDGET_SOFT_WARN_RATIO_RANGE };
  }
  return null;
}

/**
 * Soft warn only — callers must still allow submit (D11).
 */
export function evaluateBuyerBudgetGuidance(
  input: BuyerBudgetGuidanceInput,
): BuyerBudgetGuidanceResult {
  const max = input.maxPrice;
  if (max == null || !Number.isFinite(max) || max <= 0) {
    return { showSoftWarn: false, suggestedMax: null, message: null };
  }

  const resolved = resolveFloor(input);
  if (resolved == null) {
    return { showSoftWarn: false, suggestedMax: null, message: null };
  }

  const threshold = Math.floor(resolved.floor * resolved.ratio);
  if (max >= threshold) {
    return { showSoftWarn: false, suggestedMax: null, message: null };
  }

  const suggested =
    typeof input.suggestedPrice === "number" &&
    Number.isFinite(input.suggestedPrice) &&
    input.suggestedPrice > 0
      ? Math.round(input.suggestedPrice)
      : Math.round(resolved.floor);

  return {
    showSoftWarn: true,
    suggestedMax: suggested,
    message:
      "That max is well below what similar items usually sell for. You may get few alerts.",
  };
}
